import SaveTemplatePopup from '@/components/SaveTemplatePopup';
import { useSpaceAndPageIDFromRouter } from '@/data/space/hooks';
import { useCreatePageMutation, useGetSpaceQuery, useListPagesQuery, usePagesPaths } from '@/store';
import { ISpaceRoute, errToString, validUUID, IPage } from '@typedream/data';
import { useRouter } from 'next/router';
import { Dispatch, SetStateAction, useContext, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  DashboardSidenavContext,
  DashboardSidenavDispatchContext,
  PageOptionContextProvider,
} from '../../context';
import { PagesOption } from './Option';
import { PagesTreeItem } from './TreeItem';
import { PagesTreeItemHome } from './TreeItemHome';
import { MenuPagesProps } from './types';

export function MenuPages({ enableEmpty = false, enableDelete = false }: MenuPagesProps) {
  const router = useRouter();
  const [spaceID, pageID] = useSpaceAndPageIDFromRouter();
  const { data: pages } = useListPagesQuery([spaceID]);
  const pagesPaths = usePagesPaths(spaceID);

  const { openOptions, openOptionsPage } = useContext(DashboardSidenavContext);
  const { setOpenOptions, setOpenOptionsPage } = useContext(DashboardSidenavDispatchContext);

  const { data: space } = useGetSpaceQuery([spaceID]);
  const route = space?.routes;
  const { mutate: createPage } = useCreatePageMutation({ fixedCacheKey: `createPage-${spaceID}` });
  const openOptionsPath = useMemo(() => pagesPaths?.[openOptionsPage]?.path || [], [pagesPaths]);

  const onCreate = async (
    spaceRoute: ISpaceRoute,
    path: string[],
    setopen: Dispatch<SetStateAction<boolean>>
  ) => {
    try {
      const { page: newPage } = await createPage({
        spaceID,
        pagePath: path,
        routeAtPath: spaceRoute,
      }).unwrap();

      if (validUUID(newPage?.id)) {
        setopen(true);
        setOpenOptions(false);
        const spaceSlug = space?.slug;
        router.push(`/s/${spaceSlug}/${spaceID}/${newPage?.id}`);
      }
    } catch (error) {
      toast.error(errToString(error));
    }
  };

  const openPage = (path: string) => {
    setOpenOptions(false);
    router.push(path);
  };

  const onOpenOptions = (openPageID: string) => {
    setOpenOptionsPage(openPageID);
    setOpenOptions(true);
  };

  return (
    <>
      <div id="dnd-root" className="mb-[24px]">
        <PageOptionContextProvider space={space} pageID={openOptionsPage} path={openOptionsPath}>
          <PagesOption
            isOpen={openOptions}
            setOpen={setOpenOptions}
            enableEmpty={enableEmpty}
            enableDelete={enableDelete}
          />
          {pages && route && (
            <PagesTreeItemHome
              route={route}
              pages={pages}
              path={[]}
              space={space}
              pageID={pageID}
              key={route.page_id}
              onOpenOptions={onOpenOptions}
              openPage={openPage}
            />
          )}
          {pages &&
            route?.children &&
            route?.children_keys.map((childSlug) => {
              const childRoute = route?.children[childSlug];
              const isPageExist = !!pages[childRoute?.page_id];
              return isPageExist &&
                pages[childRoute?.page_id].metadata?.page_type !== 'link_in_bio' ? (
                <PagesTreeItem
                  key={childSlug}
                  route={childRoute}
                  pages={pages}
                  path={[childSlug]}
                  onCreate={onCreate}
                  space={space}
                  pageID={pageID}
                  pKey={childRoute?.page_id}
                  onOpenOptions={onOpenOptions}
                  openPage={openPage}
                  pageIDPath={pagesPaths?.[pageID]?.path}
                  isRoot
                />
              ) : null;
            })}
        </PageOptionContextProvider>
      </div>
      <SaveTemplatePopup />
    </>
  );
}
